import {
  Component,
  inject,
  signal,
  computed,
  ElementRef,
  viewChild,
  HostListener,
} from '@angular/core';
import { CommonModule, NgTemplateOutlet } from '@angular/common';
import { Router } from '@angular/router';
import { Subject, of } from 'rxjs';
import { debounceTime, switchMap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { InputComponent } from '@ams/ui-kit';
import { SearchService, SearchResult, SearchEntityType } from '../../services/search.service';
import { IconService, IconName } from '../../services/icon.service';
import { AccessControlService } from '../../services/access-control.service';
import { FeatureFlagService } from '../../services/feature-flag.service';
import { AppModule } from '../../models/access-control.model';

interface SearchCategory {
  entityType: SearchEntityType;
  module: AppModule;
  label: string; // plural — used in badges and placeholder
}

// Source of truth: tables with a `search_vector` column (organization split
// into member + chapter via org_structure, app_user, event, cms_content).
const SEARCH_CATEGORIES: SearchCategory[] = [
  { entityType: 'member', module: 'chapter.members', label: 'Members' },
  { entityType: 'chapter', module: 'tenant.chapters', label: 'Chapters' },
  { entityType: 'event', module: 'chapter.events', label: 'Events' },
  { entityType: 'user', module: 'chapter.staff', label: 'Staff' },
  { entityType: 'content', module: 'chapter.cms', label: 'Content' },
  { entityType: 'group', module: 'chapter.groups', label: 'Groups' },
  { entityType: 'resource', module: 'chapter.resources', label: 'Resources' },
  { entityType: 'pipeline', module: 'chapter.pipelines', label: 'Pipelines' },
  { entityType: 'task', module: 'chapter.tasks', label: 'Tasks' },
  { entityType: 'product', module: 'tenant.storefront', label: 'Products' },
];

const SIMPLE_ENTITY_ROUTES: Partial<Record<SearchEntityType, (id: string) => string[]>> = {
  member: (id) => ['/memberships', 'members', id],
  chapter: (id) => ['/chapters', id],
  user: (id) => ['/users', id],
  event: (id) => ['/events', id],
  content: (id) => ['/cms', 'content', id],
  group: (id) => ['/groups', id],
  pipeline: (id) => ['/engagement', 'pipelines', id],
  product: (id) => ['/store', 'products', id],
};

const ENTITY_ICONS: Record<SearchEntityType, IconName> = {
  member: 'memberships',
  chapter: 'chapters',
  user: 'staff',
  event: 'events',
  content: 'cms',
  group: 'groups',
  resource: 'resources',
  pipeline: 'pipelines',
  task: 'tasks',
  product: 'store',
};

const ENTITY_LABELS: Record<SearchEntityType, string> = {
  member: 'Member',
  chapter: 'Chapter',
  user: 'Staff',
  event: 'Event',
  content: 'Content',
  group: 'Group',
  resource: 'Resource',
  pipeline: 'Pipeline',
  task: 'Task',
  product: 'Product',
};

interface TitleFragment {
  text: string;
  match: boolean;
}

@Component({
  selector: 'app-global-search',
  standalone: true,
  imports: [CommonModule, NgTemplateOutlet, FontAwesomeModule, InputComponent],
  templateUrl: './global-search.component.html',
  styleUrl: './global-search.component.scss',
})
export class GlobalSearchComponent {
  readonly iconService = inject(IconService);
  private readonly searchService = inject(SearchService);
  private readonly router = inject(Router);
  private readonly accessControl = inject(AccessControlService);
  private readonly features = inject(FeatureFlagService);
  private readonly hostRef = inject(ElementRef<HTMLElement>);

  readonly query = signal('');
  readonly results = signal<SearchResult[]>([]);
  readonly loading = signal(false);
  readonly highlighted = signal(0);
  readonly isOpen = signal(false);
  // Multi-select scope. Empty set = search all categories.
  readonly activeCategories = signal<Set<SearchEntityType>>(new Set());

  // Pagination — "Show more" bumps the limit until we hit MAX_LIMIT (which
  // matches the backend's clamp). When `results.length === limit` we assume
  // the backend was capped and there's potentially more, so we expose the
  // button. After a fetch returns fewer rows than the current limit, the
  // button auto-hides because `canShowMore` becomes false.
  private static readonly INITIAL_LIMIT = 20;
  private static readonly LIMIT_INCREMENT = 20;
  private static readonly MAX_LIMIT = 50;
  readonly limit = signal(GlobalSearchComponent.INITIAL_LIMIT);
  readonly canShowMore = computed(
    () =>
      this.results().length === this.limit() &&
      this.limit() < GlobalSearchComponent.MAX_LIMIT,
  );

  readonly hasQuery = computed(() => this.query().trim().length > 0);
  readonly trimmedLength = computed(() => this.query().trim().length);
  readonly showDropdown = computed(() => this.isOpen());

  // Stable IDs for ARIA combobox / listbox / option wiring.
  private static instanceCounter = 0;
  private readonly instanceId = ++GlobalSearchComponent.instanceCounter;
  readonly listboxId = `global-search__listbox-${this.instanceId}`;
  optionId(index: number): string {
    return `global-search__option-${this.instanceId}-${index}`;
  }
  readonly activeOptionId = computed(() =>
    this.showingResults() ? this.optionId(this.highlighted()) : '',
  );

  // Partition results by where the match landed. A snippet with <<< markers
  // means the match was in a non-visible field (description, custom_fields,
  // etc.); otherwise the match was in title/subtitle.
  readonly directResults = computed(() =>
    this.results().filter((r) => !this.hasContentMatch(r)),
  );
  readonly contentResults = computed(() =>
    this.results().filter((r) => this.hasContentMatch(r)),
  );
  // Flat ordering used for keyboard navigation — direct matches first.
  readonly displayResults = computed(() => [
    ...this.directResults(),
    ...this.contentResults(),
  ]);
  // Show the section header(s) whenever there are any results. Even when
  // only one bucket is populated, the user wants to know whether they're
  // looking at direct matches or content matches — the label is the
  // classification, not just a visual divider.
  readonly showSectionHeaders = computed(() => this.results().length > 0);

  // Classification is title-only: a result is "Direct" when the entity's
  // primary identifier (title) contains the query term. Anything else —
  // subtitle, description, custom fields, etc. — counts as "Found in content"
  // even when the matched field is visible. The subtitle still gets its own
  // highlight so the user can see *why* the row matched.
  private hasContentMatch(r: SearchResult): boolean {
    const terms = this.query()
      .toLowerCase()
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    if (!terms.length) return false;

    const lowerTitle = r.title.toLowerCase();
    const titleMatches = terms.some((t) => lowerTitle.includes(t));
    return !titleMatches;
  }

  // Module access alone would still show Products where the storefront is off for
  // the environment: tenant.storefront is granted to the Association Admin and
  // System Admin system roles, so hasAccess() passes for every association admin.
  // The API drops the type too — this keeps the chip and placeholder honest.
  readonly categories = computed<SearchCategory[]>(() =>
    SEARCH_CATEGORIES.filter(
      (c) =>
        this.accessControl.hasAccess(c.module) &&
        (c.entityType !== 'product' || this.features.storefrontEnabled()),
    ),
  );

  readonly placeholder = computed(() => {
    const cats = this.categories();
    if (cats.length === 0) return 'Search...';
    const sample = cats.slice(0, 3).map((c) => c.label.toLowerCase());
    return `Search ${sample.join(', ')}...`;
  });

  // Body branch flags — keep template simple.
  readonly showingTypeHint = computed(() => this.trimmedLength() === 1);
  readonly showingLoader = computed(
    () => this.trimmedLength() >= 2 && this.loading() && this.results().length === 0,
  );
  readonly showingNoResults = computed(
    () => this.trimmedLength() >= 2 && !this.loading() && this.results().length === 0,
  );
  readonly showingResults = computed(
    () => this.trimmedLength() >= 2 && !this.loading() && this.results().length > 0,
  );
  readonly bodyHasContent = computed(
    () =>
      this.showingTypeHint() ||
      this.showingLoader() ||
      this.showingNoResults() ||
      this.showingResults(),
  );

  private readonly query$ = new Subject<string>();
  // `read: ElementRef` is required — the template ref points at the
  // <ams-input> component, so the default would resolve to the component
  // instance (which has no .nativeElement).
  private readonly inputHostRef = viewChild('inputHost', { read: ElementRef });
  private readonly bodyHostRef = viewChild('bodyHost', { read: ElementRef });
  private pendingBodyScrollTop: number | null = null;

  constructor() {
    this.query$
      .pipe(
        debounceTime(250),
        switchMap((q) => {
          const trimmed = q.trim();
          if (trimmed.length < 2) {
            this.loading.set(false);
            return of({ query: trimmed, results: [] });
          }
          this.loading.set(true);
          const active = this.activeCategories();
          const types = active.size > 0 ? Array.from(active) : undefined;
          return this.searchService.search(trimmed, types, this.limit());
        }),
        takeUntilDestroyed(),
      )
      .subscribe((res) => {
        this.results.set(res.results);
        this.highlighted.set(0);
        this.loading.set(false);
        this.restoreBodyScrollPosition();
      });
  }

  onValueChange(value: string): void {
    this.query.set(value);
    this.isOpen.set(true);
    this.highlighted.set(0);
    // New query — start over from the initial page size.
    this.limit.set(GlobalSearchComponent.INITIAL_LIMIT);

    if (value.trim().length === 0) {
      this.activeCategories.set(new Set());
      this.results.set([]);
      this.loading.set(false);
    }

    this.query$.next(value);
  }

  showMore(): void {
    this.pendingBodyScrollTop = this.bodyHostRef()?.nativeElement.scrollTop ?? null;
    this.limit.update((l) =>
      Math.min(l + GlobalSearchComponent.LIMIT_INCREMENT, GlobalSearchComponent.MAX_LIMIT),
    );
    // Re-fire the search with the bumped limit; debounce will fire once.
    this.query$.next(this.query());
  }

  preventButtonFocus(event: MouseEvent): void {
    event.preventDefault();
  }

  onInputFocused(): void {
    this.highlighted.set(0);
    this.isOpen.set(true);
  }

  iconFor(type: SearchEntityType): IconName {
    return ENTITY_ICONS[type] ?? 'search';
  }

  labelFor(type: SearchEntityType): string {
    return ENTITY_LABELS[type] ?? type;
  }

  isCategoryActive(type: SearchEntityType): boolean {
    return this.activeCategories().has(type);
  }

  toggleCategory(type: SearchEntityType, event?: Event): void {
    event?.stopPropagation();
    const next = new Set(this.activeCategories());
    if (next.has(type)) next.delete(type);
    else next.add(type);
    this.activeCategories.set(next);
    this.highlighted.set(0);
    // Scope change is a different result set — reset the page size too.
    this.limit.set(GlobalSearchComponent.INITIAL_LIMIT);
    // Re-fire search with the new scope so results update immediately.
    this.query$.next(this.query());
    this.nativeInput()?.focus();
  }

  highlightTitle(title: string): TitleFragment[] {
    const q = this.query().trim();
    if (!q) return [{ text: title, match: false }];
    const idx = title.toLowerCase().indexOf(q.toLowerCase());
    if (idx === -1) return [{ text: title, match: false }];
    return [
      { text: title.slice(0, idx), match: false },
      { text: title.slice(idx, idx + q.length), match: true },
      { text: title.slice(idx + q.length), match: false },
    ];
  }

  // Snippet from backend ts_headline arrives as plain text with <<<...>>>
  // markers around matched terms. Returns empty array when no markers are
  // present (the match was in title only — don't render a snippet).
  parseSnippet(snippet: string | null | undefined): TitleFragment[] {
    if (!snippet) return [];
    if (!snippet.includes('<<<')) return [];
    const parts: TitleFragment[] = [];
    const re = /<<<(.+?)>>>/g;
    let last = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(snippet)) !== null) {
      if (m.index > last) parts.push({ text: snippet.slice(last, m.index), match: false });
      parts.push({ text: m[1], match: true });
      last = re.lastIndex;
    }
    if (last < snippet.length) parts.push({ text: snippet.slice(last), match: false });
    return parts;
  }

  // Build highlighted title/snippet as a single HTML string for [innerHTML]
  // binding. Concatenating in JS avoids template whitespace appearing between
  // adjacent fragments (which causes "**North** east" instead of "**North**east").
  // All fragment text is HTML-escaped before wrapping; only <strong> tags are
  // emitted, so Angular's auto-sanitizer passes the result through.
  buildTitleHtml(title: string): string {
    return this.highlightTitle(title)
      .map((f) => (f.match ? `<strong>${this.escapeHtml(f.text)}</strong>` : this.escapeHtml(f.text)))
      .join('');
  }

  // Subtitle uses the same substring-highlight as title so users see why a
  // result matched when the hit was in a visible secondary field (e.g. a
  // user's email, an event's location) rather than the title itself.
  buildSubtitleHtml(subtitle: string | null | undefined): string {
    if (!subtitle) return '';
    return this.highlightTitle(subtitle)
      .map((f) => (f.match ? `<strong>${this.escapeHtml(f.text)}</strong>` : this.escapeHtml(f.text)))
      .join('');
  }

  buildSnippetHtml(snippet: string | null | undefined): string {
    const frags = this.parseSnippet(snippet);
    if (!frags.length) return '';
    return frags
      .map((f) => (f.match ? `<strong>${this.escapeHtml(f.text)}</strong>` : this.escapeHtml(f.text)))
      .join('');
  }

  private escapeHtml(s: string): string {
    return s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  selectResult(result: SearchResult): void {
    if (result.entityType === 'pipeline' && result.pipelineItemId && result.pipelineId) {
      this.router.navigate(['/engagement', 'pipelines', result.pipelineId], {
        queryParams: {
          itemId: result.pipelineItemId,
        },
      });
      this.afterNavigate();
      return;
    }

    const route = SIMPLE_ENTITY_ROUTES[result.entityType]?.(
      result.entityType === 'pipeline' && result.pipelineId ? result.pipelineId : result.entityId,
    );
    if (route) {
      this.router.navigate(route);
    } else if (result.entityType === 'resource') {
      this.router.navigate(['/resources'], {
        queryParams: {
          folderId: result.resourceKind === 'folder' ? result.entityId : (result.folderId ?? undefined),
          resourceId: result.resourceKind === 'item' ? result.entityId : undefined,
          groupId: result.groupId ?? undefined,
          groupName: result.groupName ?? undefined,
        },
      });
    } else if (result.entityType === 'task') {
      this.router.navigate(['/engagement', 'tasks'], {
        queryParams: {
          taskId: result.entityId,
        },
      });
    }
    this.afterNavigate();
  }

  private afterNavigate(): void {
    // Keep query, results, and scope categories — refocusing the input
    // re-opens the dropdown with the prior search intact, so users can
    // pick a different result without retyping.
    this.closeDropdown();
    this.nativeInput()?.blur();
  }

  closeDropdown(): void {
    this.isOpen.set(false);
  }

  private nativeInput(): HTMLInputElement | null {
    return this.inputHostRef()?.nativeElement.querySelector('input') ?? null;
  }

  private restoreBodyScrollPosition(): void {
    if (this.pendingBodyScrollTop == null) return;
    const scrollTop = this.pendingBodyScrollTop;
    this.pendingBodyScrollTop = null;

    setTimeout(() => {
      const body = this.bodyHostRef()?.nativeElement;
      if (body) {
        body.scrollTop = scrollTop;
      }
    });
  }

  // Keydown on the host element catches arrow nav while the inner ams-input
  // has focus (events bubble up). ams-input does not expose a (keydown) output.
  @HostListener('keydown', ['$event'])
  onHostKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeDropdown();
      this.nativeInput()?.blur();
      return;
    }

    if (!this.showDropdown()) return;

    if (this.showingResults()) {
      const items = this.displayResults();
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.highlighted.update((i) => Math.min(i + 1, items.length - 1));
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.highlighted.update((i) => Math.max(i - 1, 0));
      } else if (event.key === 'Enter') {
        const item = items[this.highlighted()];
        if (item) {
          event.preventDefault();
          this.selectResult(item);
        }
      }
    }
  }

  // ⌘K / Ctrl+K from anywhere on the page focuses the search input.
  @HostListener('document:keydown', ['$event'])
  onGlobalKeydown(event: KeyboardEvent): void {
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      const input = this.nativeInput();
      input?.focus();
      input?.select();
      this.isOpen.set(true);
    }
  }

  // Click outside the search component closes the dropdown.
  @HostListener('document:mousedown', ['$event'])
  onDocumentMouseDown(event: MouseEvent): void {
    if (!this.isOpen()) return;
    if (!this.hostRef.nativeElement.contains(event.target as Node)) {
      this.closeDropdown();
    }
  }
}
